import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, first, map, withLatestFrom } from 'rxjs/operators';

import { CmsRenderService } from '../cms-render/cms-render.service';

import { getCmsConfigValue, getContext } from '@core/infrastructure/state/core.selectors';
import { UrlsWebHelpers } from '@core/services/helpers/urls-web.helpers';
import { CategoriesSelectors } from '@features/category';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { ItxCustomEvent } from '@features/cms-render/domain/models/itx-custom-event.model';
import { Link } from '@features/cms-render/domain/models/link';
// TODO ECOMDUTI-20989 [TechDebt] - Los eventos de los Links de CMS deben ser una acción o como Output para el uso de CMS-Render
import { EventCmsMicrosite } from '@pages/microsite/store/microsite.actions';
import { MenuCategory } from '@shared/header/models/category.model';
import { NavigationService } from '@shared/services/navigation/navigation.service';

@Injectable()
export class LinksService {
    private readonly navigateBySectionConfig$ = this.store.select(getCmsConfigValue('navigateBySectionConfig'));

    constructor(
        private readonly store: Store,
        private readonly injector: Injector,
        private readonly navigationService: NavigationService,
        private readonly cmsRenderService: CmsRenderService,
    ) { }

    public getLinkUrl(link: Link): Observable<string | undefined> {
        return this.store.select(CategoriesSelectors.getMenuCategories).pipe(
            filter((categories: MenuCategory[]) => !!categories && categories.length > 0),
            first(),
            withLatestFrom(this.store.select(getContext), this.navigationService.baseUrl$),
            map(([categories, context, baseUrl]) => {
                if (link.type === 'category' && link.categoryId) {
                    let url = UrlsWebHelpers.getCategoryLocationPath(categories, link.categoryId);

                    if (url !== '' && url !== undefined) {
                        return url;
                    }
                    return UrlsWebHelpers.getCategoryUrl(context, link.categoryId);
                }
                if (link.type === 'product' && link.productId) {
                    return `${baseUrl}-l${link.productId}`;
                }
                return link.src;
            })
        );
    }

    public getUrlByConfig(link: Link, piece: ContentItemTree): Observable<string | undefined> {
        let value = link?.src;
        // is section link
        if (!!link?.section) {
            value = link?.section;
        }
        // is category or product link
        if (link?.type === 'category' || link?.type === 'product') {
            const pieceName = piece ? piece.name : '';
            const _link = { pieceName, ...link };
            return this.getLinkUrl(_link);
        }

        // is producto link
        if (link?.type === 'menu') {
            // no se necesita imprimir nada en el href
            return of(null);
        }

        // is custom section link
        if (link?.src?.includes('customSection')) {
            value = link?.src.split('=').pop();
        }
        // is product or category link
        if (
            !link?.type?.includes('external') && !link?.relativeLink && !link?.src?.includes('customSection') && !link.section
        ) {
            const pieceName = piece ? piece.name : '';
            const _link = { pieceName, ...link };
            return this.getLinkUrl(_link);
        }

        // is external or internal link
        const navigationService = this.injector.get(NavigationService);
        return combineLatest([
            this.navigateBySectionConfig$,
            navigationService.baseUrl$,
        ]).pipe(
            map(([configJson, baseUrl]) => {
                if (value?.includes('http')) {
                    return value;
                }
                const slug = value.replace(baseUrl.slice(1), '');

                if (!configJson[slug]?.url) {
                    let keyByUrl: any = Object.entries(configJson).find((el: any) => {
                        return el[1].url === slug;
                    });
                    const _path = keyByUrl ? keyByUrl[1].url : slug;
                    return `${baseUrl}${_path}`;
                }
                return `${baseUrl}${configJson[slug].url}`;
            })
        );
    }

    public openLink($event: MouseEvent, link: Link, contentItemTree: ContentItemTree): void {
        switch (link.type) {
            case 'menu':
                this.openMenu($event, link);
                break;
            case 'category':
                this.openCategory($event, link);
                break;
            case 'product':
                this.openProduct($event, link);
                break;
            case 'section':
                this.openSection($event, link, contentItemTree);
                break;
            default:
                this.goToRoute($event, link, contentItemTree);
        }
    }

    private openProduct($event: Event, link: Link): void {
        $event.preventDefault();
        this.cmsRenderService.openShopTheLookModal(link.productId, link.categoryId);
    }

    private openSection($event: Event, link: Link, contentItemTree: ContentItemTree): void {
        $event.preventDefault();
        $event.stopPropagation();
        const linkSection = link?.src?.includes('customSection') ? link?.src?.split('=').pop() : link?.section;
        this.cmsRenderService.dispatchSectionEvents(link?.categoryId, link.type, '', linkSection, link.newWindow, contentItemTree.slug);
    }

    private openCategory($event: Event, link: Link): void {
        $event.preventDefault();
        $event.stopPropagation();
        this.cmsRenderService.dispatchCategoriesEvents(link?.pieceName, link?.categoryId, link.type, '', link.newWindow);
    }

    private openMenu($event: Event, link: Link): void {
        $event.preventDefault();
        this.cmsRenderService.dispatchMenuEvents(link?.categoryId, link.type, '');
    }

    private goToRoute(event: Event, link: Link, contentItemTree: ContentItemTree): void {
        event.preventDefault();
        event.stopPropagation();
        if (link.src.includes('customEventLive')) {
            this.store.dispatch(EventCmsMicrosite({ url: link.src }));
            return;
        }
        if (link.src.includes('customEvent')) {
            const myEvent: ItxCustomEvent = {
                detail: {
                    extra: link,
                },
            };
            this.cmsRenderService.dispatchManagement(myEvent);
            this.cmsRenderService.dispatchExternalEvent(link?.categoryId, link.type, '', link?.src, link.newWindow, contentItemTree.slug);
            return;
        }
        if (link?.src.includes('customSection')) {
            this.openSection(event, link, contentItemTree);
        } else {
            this.cmsRenderService.dispatchExternalEvent(link?.categoryId, link.type, '', link?.src, link.newWindow, contentItemTree.slug, link?.relativeLink);
        }
    }
}
